import React, { useEffect, useMemo } from "react";
import find from "lodash/find";
import times from "lodash/times";
import submit from "submit-form";

import WorkoutPreview from "./components/workout-preview";

import SvgIcon from "../../../parts/SvgIcon";
import { truncateText } from "../../../utils/string-helpers";

const formatExercises = (exercises, user) => {
  const newWorkouts = {};
  Object.keys(exercises).map((weekSession) => {
    Object.keys(exercises[weekSession]).map((key) => {
      const newExercises = exercises[weekSession][key].map((exercise) => {
        exercise.repMaximums = user.repMaximums;
        exercise.sets = exercise.sets.map((set) => {
          if (set.hasOwnProperty("percentRepMax")) {
            let repMax = 0;

            // if exercise is primary then just use the standard repMax
            if (exercise.type === "primary") {
              repMax = user.repMaximums[exercise.primary];
            } else {
              // find the secondary exercise in the users library
              let secondaryExercise = find(
                user.exerciseLibrary[exercise.primary],
                { movement: exercise.secondary }
              );
              // if the exercise isn't in the user's existing library then add it with their
              // primary repMax
              if (!secondaryExercise) {
                // create the secondary exercise and add it to the user
                secondaryExercise = {
                  movement: exercise.secondary,
                  repMaximum: user.repMaximums[exercise.primary],
                };
              }

              repMax = secondaryExercise.repMaximum;

              // save the Secondary RM on the exercise
              exercise.secondaryRepMaximum = repMax;
            }
            // finally caulate the load of the set
            set.load = (set.percentRepMax / 100) * repMax;
          }
          return set;
        });

        return exercise;
      });
      newWorkouts[weekSession] = {
        ...newWorkouts[weekSession],
        [key]: newExercises,
      };
    });
  });

  return newWorkouts;
};

const handleProgramChange = (program) => {
  let programPath = `/actions/refresh?week=1`;
  submit(programPath, {
    body: { program: program + 1 },
  });
};

function View(props, context) {
  const { locals } = context;

  const {
    editCycle: cycle,
    cycleExercises,
    activeProgramIndex,
    activeUser,
    query,
    heading,
    activeUserToRequest,
  } = locals;

  const program = cycle.programs[activeProgramIndex];
  const sessions = program && program.sessions ? program.sessions : [];

  const isCoach = cycle.type === "coach";
  const isSubscription = cycle.type === "subscription";
  const isAthlete =
    (activeUser.coach && !activeUser.coach.inactive) ||
    Boolean(activeUserToRequest.subscription);
  const isPushCoach = Boolean(activeUserToRequest.pushAccountInfo);
  const inCoachMode =
    isCoach &&
    String(cycle.createdBy._id) === String(activeUserToRequest._id) &&
    heading !== "Program Builder";
  const inPushCoachMode =
    isSubscription &&
    String(cycle.createdBy._id) === String(activeUserToRequest._id) &&
    heading !== "Program Builder";
  // If the user is a coach that is in the Athlete Portal:
  // Return the Athlete's load. Otherwise, return the Coach's load.
  const newExercises = formatExercises(
    cycleExercises.exercises,
    query.athlete ? locals.athlete : activeUserToRequest
  );

  let organizationQuery = ``;

  if (query.organizationCoach) {
    organizationQuery = `&organizationCoach=${query.organizationCoach}`;
  }

  let backPath = inCoachMode
    ? "/coaching-portal/all-templates?owner=coach"
    : "/dashboard/program-library";

  if (query.athlete) {
    backPath =
      `/athlete-portal/programs/?athlete=${query.athlete}` + organizationQuery;
  } else if (isPushCoach && inPushCoachMode) {
    const isPublished = cycle.status === "published";
    const storeType = cycle.storeType || "programs";

    backPath = `/coaching-portal/store?status=${
      isPublished ? "published" : "draft"
    }&storeType=${storeType}`;
  }

  if (isAthlete) {
    backPath = "/dashboard/program-builder";
  }

  const count = useMemo(
    () =>
      program.sessions.reduce((prev, session) => {
        if (!prev.includes(session.week)) return [...prev, session.week];
        return prev;
      }, []).length,
    [program]
  );

  useEffect(() => {
    setExerciseListHeight();
  }, []);

  const setExerciseListHeight = () => {
    for (let n = 0; n < 7; n++) {
      const className = `workout-${n + 1}-list`;
      // gets one row of workouts
      const elements = Array.from(document.getElementsByClassName(className));

      elements.forEach((el) => {
        el.style.height = "auto";
      });
      const elementsHeights = elements.map((el) => el.clientHeight);
      const maxHeight = Math.max(...elementsHeights);
      // set the height of all elements in the row to the same height as the tallest element in it
      elements.forEach((el) => {
        el.style.height = `${maxHeight}px`;
      });
    }
  };

  const notSingleWorkout = cycle.storeType !== "single-workout";

  return (
    <main className="program-preview">
      <header>
        <hgroup>
          <a href={backPath}>
            <button data-button-type="tertiary" data-icon-only>
              <SvgIcon src={"/img/new-icons/arrow-square-left.svg"} />
            </button>
            <span>Preview</span>
          </a>
        </hgroup>

        {notSingleWorkout && (
          <hgroup>
            <div role="month-handler">
              <div>
                <button
                  onClick={() => handleProgramChange(activeProgramIndex - 1)}
                  disabled={activeProgramIndex === 0}
                  data-button-type="text-white"
                  data-icon-only
                >
                  <SvgIcon src="/img/new-icons/arrow-left.svg" alt="prev" />
                </button>
                <span className="flex-shrink-0">
                  {truncateText(cycle?.programs[activeProgramIndex]?.name, 50)}
                </span>
                <button
                  onClick={() => handleProgramChange(activeProgramIndex + 1)}
                  disabled={activeProgramIndex === cycle?.programs.length - 1}
                  data-button-type="text-white"
                  data-icon-only
                >
                  <SvgIcon src="/img/new-icons/arrow-right.svg" alt="next" />
                </button>
              </div>
            </div>
          </hgroup>
        )}

        <hgroup></hgroup>
      </header>
      <div className="Workout-Area">
        <div className="weeks">
          {times(count).map((weekNumber) => {
            return (
              <div
                className="week"
                key={`${cycle._id}-program${activeProgramIndex}-week${
                  weekNumber + 1
                }`}
              >
                {notSingleWorkout && (
                  <header>
                    <p className="s2 clipped">Week {weekNumber + 1}</p>
                  </header>
                )}
                {newExercises[weekNumber + 1] &&
                  Object.keys(newExercises[weekNumber + 1]).map(
                    (workoutNumber) => {
                      return (
                        <WorkoutPreview
                          locals={locals}
                          workoutNumber={workoutNumber}
                          exercises={
                            newExercises[weekNumber + 1][workoutNumber]
                          }
                          sessions={sessions}
                          key={`${cycle._id}-program${activeProgramIndex}-week${
                            weekNumber + 1
                          }-workout${workoutNumber}`}
                          notSingleWorkout={notSingleWorkout}
                        />
                      );
                    }
                  )}
              </div>
            );
          })}
        </div>
      </div>
    </main>
  );
}

export default View;
